import { Injectable } from "@angular/core";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Graphic from "@arcgis/core/Graphic";
import Polygon from "@arcgis/core/geometry/Polygon";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import ClassBreaksRenderer from "@arcgis/core/renderers/ClassBreaksRenderer";
import Color from "@arcgis/core/Color";
import {
  TemperatureArea,
  TemperatureLevel,
  TemperatureUnitEntity,
} from "@gishub/domain";
import { BaseNotifier } from "../../../../shared/notifiers/base-notifier";
import { Ref } from "../../../../shared/providers/ref";
import { TemperatureRepositoryProvider } from "../../../../shared/providers/repository-providers";
import { MapProvider } from "../map/map-provider";
import { ScaleProvider } from "../../services/scale-provider";
import { NowcastingProvider } from "../nowcasting/nowcasting-provider";
import { WarningProvider } from "../warning/warning-provider";
import { SenamhiLayerNotifier } from "../senamhi/senamhi-layer-notifier";
import { TemperatureState } from "./temperature-state";
import { TEMPERATURE_BANDS } from "./temperature-band";

/**
 * Temperatura por unidad territorial sobre las zonas de alerta SENAMHI.
 *
 * Reglas de negocio:
 *  • Sólo se muestran unidades que **intersecan** una zona de alerta activa
 *    (nowcasting o aviso). Sin zona activa, la capa se retira.
 *  • El nivel cambia con el zoom: lejos → **departamento**, cerca → **provincia**.
 *  • El dato se resuelve en UNA petición por nivel (Open-Meteo multiconsulta)
 *    y se dibuja como coropleto + etiqueta compacta "NOMBRE 24°C".
 */
@Injectable()
export class TemperatureNotifier extends BaseNotifier<TemperatureState> {
  private static readonly INITIAL: TemperatureState = {
    active: false,
    level: "department",
    units: [],
    loading: false,
    error: null,
    updatedAt: null,
  };

  /**
   * Escala (1:X) a partir de la cual la vista pasa al nivel provincial.
   * Por debajo de este denominador (más cerca) manda la provincia; por encima
   * (más lejos, nacional/regional) manda el departamento.
   */
  private static readonly PROVINCE_SCALE = 1_500_000;

  /** Tiempo de espera antes de recalcular (evita ráfagas durante el zoom). */
  private static readonly DEBOUNCE_MS = 160;

  private readonly _layers: {
    department: FeatureLayer | null;
    province: FeatureLayer | null;
  } = { department: null, province: null };

  private _hasDepartment = false;
  private _hasProvince = false;

  /** Firma de la última consulta resuelta; null = sin zona activa. */
  private _lastKey: string | null = null;

  /** Token monotónico para descartar respuestas obsoletas en vuelo. */
  private _token = 0;

  private _debounce: number | null = null;

  /** Últimas unidades resueltas por nivel (para el estado al cambiar de zoom). */
  private _departmentUnits: TemperatureUnitEntity[] = [];
  private _provinceUnits: TemperatureUnitEntity[] = [];

  constructor(ref: Ref) {
    super(ref, TemperatureNotifier.INITIAL);
  }

  // ── Lifecycle ─────────────────────────────────────────────────

  override build(): void {
    this.listen(() => {
      const mapState = this.ref.read(MapProvider).state();
      // La escala fiable vive en ScaleProvider (watch real de `view.scale`);
      // leerla aquí crea la dependencia que reevalúa dpto↔provincia al hacer zoom.
      this.ref.read(ScaleProvider).state();
      if (!mapState.map || !mapState.view) return;
      // Reactividad: los estados SENAMHI añaden sus capas visibles y su
      // `refreshEpoch` (una capa re-sincronizada vuelve a disparar el sync).
      this.ref.read(NowcastingProvider).state();
      this.ref.read(WarningProvider).state();
      this.scheduleSync();
    });
  }

  protected override onDispose(): void {
    this.clearDebounce();
    this.removeLayers();
  }

  // ── Orquestación ──────────────────────────────────────────────

  private scheduleSync(): void {
    this.clearDebounce();
    this._debounce = window.setTimeout(() => {
      this._debounce = null;
      void this.sync();
    }, TemperatureNotifier.DEBOUNCE_MS);
  }

  private clearDebounce(): void {
    if (this._debounce != null) {
      window.clearTimeout(this._debounce);
      this._debounce = null;
    }
  }

  private async sync(): Promise<void> {
    if (this._disposed) return;
    const mapState = this.ref.read(MapProvider).state();
    if (!mapState.map || !mapState.view) return;

    const areas = this.collectAreas();

    // Sin zona de alerta activa: fuera del mapa.
    if (areas.length === 0) {
      if (this._lastKey !== null) {
        this._lastKey = null;
        this.removeLayers();
        this.updateState((s) => ({
          ...s,
          active: false,
          units: [],
          loading: false,
          error: null,
          updatedAt: null,
        }));
      }
      return;
    }

    const key = this.areasKey(areas);
    if (key === this._lastKey) {
      // Misma zona: basta con reevaluar el nivel por escala.
      this.applyScale();
      return;
    }
    this._lastKey = key;

    const token = ++this._token;
    this.updateState((s) => ({ ...s, active: true, loading: true, error: null }));

    try {
      const repo = this.ref.read(TemperatureRepositoryProvider).instance;
      const [departments, provinces] = await Promise.all([
        repo.getUnits({ level: "department", areas }),
        repo.getUnits({ level: "province", areas }),
      ]);
      if (this._disposed || token !== this._token) return;

      const depUnits = departments.success ? departments.data ?? [] : [];
      const provUnits = provinces.success ? provinces.data ?? [] : [];

      this.render(depUnits, provUnits);

      const level = this.snapshot().level;
      this.updateState((s) => ({
        ...s,
        active: true,
        loading: false,
        units: level === "province" ? provUnits : depUnits,
        error:
          !departments.success && !provinces.success
            ? departments.message ?? "No se pudo obtener la temperatura"
            : null,
        updatedAt: Date.now(),
      }));
    } catch (error) {
      if (this._disposed || token !== this._token) return;
      this.updateState((s) => ({
        ...s,
        loading: false,
        error: (error as Error)?.message ?? "Error al obtener la temperatura",
      }));
    }
  }

  /**
   * Polígonos de las capas SENAMHI visibles (nowcasting + avisos). Cada
   * `RawFeature` ya trae sus anillos en WGS84, listos para el cruce espacial.
   */
  private collectAreas(): TemperatureArea[] {
    const areas: TemperatureArea[] = [];
    const append = (notifier: SenamhiLayerNotifier<any> | null | undefined): void => {
      if (!notifier) return;
      for (const key of notifier.state().visibleItems) {
        for (const feature of notifier.getFeaturesForLayer(key) ?? []) {
          if (feature?.rings?.length) areas.push(feature.rings);
        }
      }
    };
    append(this.ref.read(NowcastingProvider).notifier);
    append(this.ref.read(WarningProvider).notifier);
    return areas;
  }

  /** Firma compacta de la zona activa (detecta cambios de aviso/horizonte). */
  private areasKey(areas: TemperatureArea[]): string {
    const parts: string[] = [];
    let vertices = 0;
    for (const area of areas) {
      const ring = area[0] ?? [];
      const first = ring[0] ?? [0, 0];
      vertices += ring.length;
      parts.push(
        `${ring.length}:${Math.round((first[0] ?? 0) * 1e3)}:${Math.round(
          (first[1] ?? 0) * 1e3,
        )}`,
      );
    }
    return `${areas.length}|${vertices}|${parts.join(",")}`;
  }

  // ── Zoom → nivel ──────────────────────────────────────────────

  /** Muestra un nivel u otro según la escala y mantiene las capas arriba. */
  private applyScale(): void {
    const mapState = this.ref.read(MapProvider).state();
    if (!mapState.map) return;
    const scale = this.ref.read(ScaleProvider).state().scale ?? 0;
    const showProvince = scale > 0 && scale <= TemperatureNotifier.PROVINCE_SCALE;

    if (this._layers.department) {
      this._layers.department.visible = !showProvince && this._hasDepartment;
    }
    if (this._layers.province) {
      this._layers.province.visible = showProvince && this._hasProvince;
    }

    this.bringToFront();

    const level: TemperatureLevel = showProvince ? "province" : "department";
    if (this.snapshot().level !== level) {
      const units = level === "province" ? this._provinceUnits : this._departmentUnits;
      this.updateState((s) => ({ ...s, level, units }));
    }
  }

  /** Reinserta las capas al tope: SENAMHI re-añade las suyas tras cada sync. */
  private bringToFront(): void {
    const map = this.ref.read(MapProvider).state().map;
    if (!map) return;
    for (const layer of [this._layers.department, this._layers.province]) {
      if (layer && map.layers.includes(layer)) {
        map.reorder(layer, map.layers.length - 1);
      }
    }
  }

  // ── Render ────────────────────────────────────────────────────

  private render(
    departments: TemperatureUnitEntity[],
    provinces: TemperatureUnitEntity[],
  ): void {
    const mapState = this.ref.read(MapProvider).state();
    if (!mapState.map) return;

    this.removeLayers();
    this._departmentUnits = departments;
    this._provinceUnits = provinces;

    const depLayer = this.buildLayer(
      "temperature-department",
      "Temperatura por departamento",
      departments,
    );
    const provLayer = this.buildLayer(
      "temperature-province",
      "Temperatura por provincia",
      provinces,
    );

    this._hasDepartment = !!depLayer;
    this._hasProvince = !!provLayer;
    if (depLayer) mapState.map.add(depLayer);
    if (provLayer) mapState.map.add(provLayer);
    this._layers.department = depLayer;
    this._layers.province = provLayer;

    this.applyScale();
  }

  private buildLayer(
    id: string,
    title: string,
    units: TemperatureUnitEntity[],
  ): FeatureLayer | null {
    const withGeometry = units.filter((unit) => this.ringsOf(unit) != null);
    if (withGeometry.length === 0) return null;

    const graphics = withGeometry.map((unit, index) =>
      new Graphic({
        geometry: new Polygon({
          rings: this.ringsOf(unit)!,
          spatialReference: new SpatialReference({ wkid: 4326 }),
        }),
        attributes: {
          OBJECTID: index + 1,
          NOMBRE: unit.name,
          TEMPERATURA: unit.temperature ?? null,
          ETIQUETA:
            unit.temperature == null
              ? unit.name
              : `${unit.name}\n${Math.round(unit.temperature)}°C`,
        },
      }),
    );

    return new FeatureLayer({
      id,
      title,
      source: graphics,
      geometryType: "polygon",
      visible: false,
      listMode: "hide",
      objectIdField: "OBJECTID",
      fields: [
        { name: "OBJECTID", type: "oid" },
        { name: "NOMBRE", type: "string" },
        { name: "TEMPERATURA", type: "double" },
        { name: "ETIQUETA", type: "string" },
      ],
      renderer: this.buildRenderer(),
      labelingInfo: [this.buildLabelClass()],
    });
  }

  private ringsOf(unit: TemperatureUnitEntity): number[][][] | null {
    const rings = (unit.geometry as { rings?: number[][][] } | null)?.rings;
    return Array.isArray(rings) && rings.length > 0 ? rings : null;
  }

  /**
   * Sólo contorno: el relleno del coropleto tapaba los colores de las zonas
   * SENAMHI. El color del borde comunica la banda térmica sin ocultar la alerta.
   */
  private buildRenderer(): ClassBreaksRenderer {
    return new ClassBreaksRenderer({
      field: "TEMPERATURA",
      classBreakInfos: TEMPERATURE_BANDS.map((band) => ({
        minValue: band.min,
        maxValue: band.max,
        symbol: new SimpleFillSymbol({
          color: new Color([0, 0, 0, 0]),
          outline: new SimpleLineSymbol({
            color: new Color([...this.hexToRgb(band.color), 1]),
            width: 0.9,
          }),
        }),
      })),
      defaultSymbol: new SimpleFillSymbol({
        color: new Color([0, 0, 0, 0]),
        outline: new SimpleLineSymbol({
          color: new Color([120, 120, 120, 0.55]),
          width: 0.7,
        }),
      }),
    });
  }

  /**
   * Etiqueta compacta: nombre + temperatura, sin caja, con un halo claro que
   * la mantiene legible sobre cualquier mapa base sin recargar la vista.
   */
  private buildLabelClass(): any {
    return {
      labelExpressionInfo: { expression: "$feature.ETIQUETA" },
      labelPlacement: "always-horizontal",
      deconflictionStrategy: "static",
      symbol: {
        type: "text",
        color: [8, 38, 44, 1],
        haloColor: [255, 255, 255, 0.85],
        haloSize: 1.2,
        font: { family: "sans-serif", size: 9, weight: "bold" },
      },
    };
  }

  private hexToRgb(hex: string): [number, number, number] {
    const h = hex.replace("#", "");
    return [
      parseInt(h.substring(0, 2), 16),
      parseInt(h.substring(2, 4), 16),
      parseInt(h.substring(4, 6), 16),
    ];
  }

  // ── Limpieza ──────────────────────────────────────────────────

  private removeLayers(): void {
    const map = this.ref.read(MapProvider).state().map;
    for (const layer of [this._layers.department, this._layers.province]) {
      if (!layer) continue;
      if (map) map.remove(layer);
      try {
        layer.destroy();
      } catch {
        /* ignore */
      }
    }
    this._layers.department = null;
    this._layers.province = null;
    this._hasDepartment = false;
    this._hasProvince = false;
  }
}
